import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { format, startOfDay, addDays } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';

const EVENT_TYPE_COLORS = {
  visit: '#3b82f6', errand: '#f59e0b', google: '#10b981',
  birthday: '#ec4899', invoice: '#ef4444', expiration: '#f97316',
  vacation: '#8b5cf6', absence: '#6b7280',
};

export const meta = {
  id: 'upcoming_events',
  category: 'calendar',
  titleKey: 'dashboard.widgets.upcomingEvents',
  descriptionKey: 'dashboard.widgets.upcomingEventsDesc',
  defaultSize: 'xl',
  requiresModule: 'calendar',
  icon: Calendar,
};

export default function UpcomingEventsWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const locale = user?.language === 'en' ? enUS : es;

  const eventsFrom = format(startOfDay(new Date()), 'yyyy-MM-dd');
  const eventsTo = format(addDays(new Date(), 7), 'yyyy-MM-dd');
  const { data: upcomingEvents = [] } = useQuery({
    queryKey: ['dashboard-events', eventsFrom, eventsTo],
    queryFn: () => api.get(`/calendar/events?from=${eventsFrom}&to=${eventsTo}`).then(r => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
      <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calendar size={16} />
          {t('dashboard.upcomingEvents')}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/calendar')}>
          {t('dashboard.viewCalendar')} →
        </button>
      </div>
      {upcomingEvents.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
          {t('dashboard.noUpcomingEvents')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '280px' : '340px'}, 1fr))`, gap: 0 }}>
          {[...upcomingEvents]
            .sort((a, b) => new Date(a.start) - new Date(b.start))
            .slice(0, 8)
            .map((ev, i) => {
              const evDate = new Date(ev.start);
              const isAllDay = ev.allDay;
              const color = EVENT_TYPE_COLORS[ev.type] || '#6b7280';
              return (
                <div
                  key={ev.id || i}
                  onClick={() => navigate('/calendar')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 20px',
                    borderBottom: '1px solid var(--c-border-subtle)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <div style={{ width: 4, height: 28, borderRadius: 2, background: color, flexShrink: 0 }} />
                  <div style={{ width: 52, flexShrink: 0, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--c-text-subtle)', textTransform: 'uppercase', fontWeight: 600 }}>
                      {format(evDate, 'EEE', { locale })}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>
                      {format(evDate, 'd')}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>
                      {isAllDay ? t('dashboard.allDay') : format(evDate, 'HH:mm')}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.title}
                    </div>
                    {ev.company && (
                      <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 1 }}>
                        {ev.company}
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 999,
                    background: color + '18', color, fontWeight: 600, flexShrink: 0,
                  }}>
                    {ev.type}
                  </span>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
